/**
 * `TaskPeekHost` — the `?task=<id>` DOORWAY, and nothing else.
 *
 * WHAT USED TO BE HERE. A 750-line `TaskPeek`: a second full peek, for one kind.
 * It carried its own click-to-edit title, its own click-to-edit description, a
 * hand-rolled Status / Owner / Priority / Due row set, its own "Details" group,
 * its own pinned action row, and a `TaskPeekActions` prop bag threading seven
 * server actions through the block boundary. Every change to how a peek looks
 * had to be made twice, and the two copies drifted exactly as you would expect —
 * both grew the same `md:type-body` masthead bug independently, and only one of
 * them ever got the properties-first arrangement.
 *
 * IT IS GONE. `task` is an entry in `KIND_EDITORS`
 * (`excellent/src/modes/object-editors.ts`) like every other kind, so an issue
 * is rendered by the same `ObjectPeek` that renders a deal, from descriptors.
 * The editable title, the edit-in-place description, the 32px property controls,
 * the activity rail, the static footer and the destructive control are all the
 * shared implementation. The task-only CAPABILITIES (auto-verify runs, linked
 * commits, the goal link) ride the peek's extension slots from
 * `excellent/app/_chrome/task-extensions.tsx`.
 *
 * WHY THIS FILE STILL EXISTS. `?task=<id>` is written by a lot of surfaces —
 * task cards, the issues table, the board adapter, the context menu, the review
 * inbox, the Work queue — and those links are addressed by the task's LOCAL
 * INTEGER id, while the universal peek is addressed by ULID. Rather than rewrite
 * every call site (and break every pasted `?task=` link in the wild), this
 * resolves the id to its uid and hands off to the one peek. The `?task=` address
 * stays valid; there is just no second peek behind it.
 */

import * as React from "react";
import { usePathname, useNavigate as useRouter, useSearch as useSearchParams } from "../../router";
import { useApi } from "../../api/client";
import { tasks } from "../../api/contracts/tasks";
import { ACTIVE_PANE_PARAM, PEEK_PARAM } from "../object-link";

/**
 * Mount exactly ONCE, in the root layout, beside `ObjectPeekHost`. It renders
 * nothing: its whole job is to translate the legacy address into the canonical
 * one, at which point `ObjectPeekHost` (watching `?peek=`) takes over.
 *
 * The swap is a `replace`, not a `push`, so Back still leaves the peek rather
 * than bouncing between two spellings of the same address.
 */
export function TaskPeekHost() {
  const search = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const raw = search.get("task");
  const taskId = raw && /^\d+$/.test(raw) ? Number(raw) : null;

  // The scoped task read IS the id→uid resolver. It is the same SWR key the task
  // extensions use, so opening an issue is still one request for the row.
  const { data: task } = useApi(
    tasks.get,
    taskId != null ? { params: { id: taskId } } : null,
  );
  const uid = task?.uid ?? null;

  React.useEffect(() => {
    if (taskId == null || !uid) return;
    const next = new URLSearchParams(search.toString());
    next.delete("task");
    next.set(PEEK_PARAM, uid);
    next.set(ACTIVE_PANE_PARAM, "inspector");
    router.replace(`${pathname}?${next.toString()}`);
  }, [taskId, uid, search, router, pathname]);

  return null;
}
